import { Context } from "components/Scenario/utils";
import { TaggedItemUnion } from "./types";
import { assert, contextReplace } from "./utils";
import type { Duration } from "moment";
import moment from "moment";
import i18next from "i18next";

type AccessExpression = TaggedItemUnion<{
  name: string;
  subname: string; // substitute name
  value: Expression;
}>[];
type Expression = Boolean;
type Boolean = [Equality, { type: BooleanOp; item: Equality }[]];
type Equality = [Sum, { type: EqualityOp; item: Sum }[]];
type Sum = [Product, { type: "+" | "-"; item: Product }[]];
type Product = [Prefix, { type: "*" | "/" | "??"; item: Prefix }[]];
type MatchPattern =
  | TaggedItemUnion<{
      condition: { name: string; expr: Expression };
      union: { patterns: MatchPattern[] };
    }>
  | Value;
type Prefix = [
  value: Value,
  operator?: {
    type:
      | "translate"
      | "castNumber"
      | "castString"
      | "castDate"
      | "round"
      | "ceil"
      | "floor"
      | "not";
  }
];
type Value = TaggedItemUnion<{
  measure: { value: number; measure: Measure };
  num: number;
  bool: boolean;
  str: string;
  name: AccessExpression;
  match: {
    patterns: { pattern: MatchPattern; expr: Expression }[];
    value: Expression;
  };
  object: { key: string; value: Expression }[];
  array: Expression[];
  map: { value: Expression; name: string; expr: Expression };
  expr: Expression;
}>;
type EvalValue = number | boolean | string | Date | object | Duration | EvalValue[];

// years months days hours minutes seconds
const MEASURES = ["y", "mon", "d", "h", "min", "s", "ms"] as const;
const BOOLEAN_OPS = ["and", "or"] as const;
const EQUALITY_OPS = ["=", "<", ">", "!=", ">=", "<=", "in", "not_in"] as const;
const PREFIX_OPS = {
  number: "castNumber",
  string: "castString",
  date: "castDate",
  round: "round",
  ceil: "ceil",
  floor: "floor",
  not: "not",
  translate: "translate",
} as const;
type Measure = typeof MEASURES[number];
type BooleanOp = typeof BOOLEAN_OPS[number];
type EqualityOp = typeof EQUALITY_OPS[number];
type PrefixOp = keyof typeof PREFIX_OPS;

type Token =
  | BooleanOp
  | "??"
  | "+"
  | "-"
  | "*"
  | "/"
  | "("
  | ")"
  | "."
  | ","
  | "\\"
  | "\\("
  | ":"
  | "{"
  | "}"
  | "match"
  | "|"
  | "map"
  | "["
  | "]"
  | { type: "measure"; item: { value: number; measure: Measure } }
  | { type: "num"; item: number }
  | { type: "str"; item: string }
  | { type: "name"; item: string }
  | { type: "bool"; item: boolean };

export const nextToken = (
  str: string,
  index: number = 0
): [token: Token, index: number] => {
  while (/\s/.test(str.charAt(index))) {
    ++index;
  }
  if (/[0-9]/.test(str.charAt(index))) {
    let val = 0;
    while (/[0-9]/.test(str.charAt(index))) {
      val = 10 * val + Number(str.charAt(index++));
    }

    if (/[a-z]/.test(str.charAt(index))) {
      let i = index;
      let name = "";
      while (/[a-z]/.test(str.charAt(i))) {
        name += str.charAt(i++);
      }

      if (MEASURES.includes(name as Measure))
        return [{ type: "measure", item: { value: val, measure: name } } as Token, i];
    }

    return [{ type: "num", item: val }, index];
  }
  if (/[a-zA-Z_]/.test(str.charAt(index))) {
    let name = "";
    while (/[0-9a-zA-Z_]/.test(str.charAt(index))) {
      name += str.charAt(index++);
    }

    if (["always", "true", "false"].includes(name))
      return [{ type: "bool", item: name !== "false" }, index];
    if (["or", "and", "match", "in", "not_in"].includes(name))
      return [name as Token, index];
    return [{ type: "name", item: name }, index];
  }

  if (str.charAt(index) === '"') {
    let _str = "";
    ++index;
    while (str.charAt(index) !== '"') {
      if (str.charAt(index) === "") throw new Error("Unterminated string");
      else if (str.charAt(index) === "\\") {
        ++index;
        _str += str.charAt(index++);
      } else _str += str.charAt(index++);
    }
    return [{ type: "str", item: _str }, ++index];
  }
  if (["!=", ">=", "<=", "\\(", "??"].includes(str.slice(index, index + 2))) {
    return [str.slice(index, index + 2) as Token, index + 2];
  }
  return [str.charAt(index) as Token, index + 1];
};

export function* groupBy<
  T extends unknown,
  U extends unknown,
  P extends (tokens: T[], index: number) => [U, number]
>(str: T[], parse: P) {
  let index = 0;

  while (str[index] !== "") {
    const [token, nextIndex] = parse(str, index);
    index = nextIndex;
    yield token;
  }
}

export function* tokens(str: string) {
  let index = 0;

  while (str.charAt(index) !== "") {
    const [token, nextIndex] = nextToken(str, index);
    index = nextIndex;
    yield token;
  }
}

export const parseAccessExpression = (
  str: Token[],
  index: number = 0
): [val: AccessExpression, index: number] => {
  const items: AccessExpression = [];
  const parseNextItem = () => {
    const token = str[index];

    if (typeof token === "object" && (token.type === "name" || token.type === "num")) {
      ++index;
      items.push({ item: token.item.toString(), type: "name" });
      return;
    }
    if (token === "\\(") {
      const [res, i] = parseExpr(str, index + 1);
      const token = str[i];
      if (token !== ")") throw new Error("failed to parse: missing closing parens");
      index = i + 1;
      items.push({ type: "value", item: res });
      return;
    }
    if (token === "\\") {
      const token = str[index + 1];
      if (!(typeof token === "object" && token.type === "name"))
        throw new Error("failed to parse: expected name");
      index = index + 2;
      items.push({ ...token, type: "subname" });
      return;
    }
    throw new Error("Unexpected token: " + token);
  };
  parseNextItem();

  while (true) {
    const token = str[index];
    if (token !== ".") break;
    ++index;
    parseNextItem();
  }
  return [items, index];
};

export const parseExpr = (
  str: Token[],
  index: number = 0
): [val: Expression, index: number] => {
  return parseBoolean(str, index);
};

export const parseBoolean = (
  str: Token[],
  index: number = 0
): [val: Boolean, index: number] => {
  const rest: Boolean[1] = [];
  const [sum, i] = parseEquality(str, index);
  index = i;
  while (true) {
    const token = str[index];
    if (!BOOLEAN_OPS.includes(token as typeof BOOLEAN_OPS[number])) break;
    const [restSum, i] = parseEquality(str, index + 1);
    rest.push({ type: token as typeof BOOLEAN_OPS[number], item: restSum });
    index = i;
  }
  return [[sum, rest], index];
};

export const parseEquality = (
  str: Token[],
  index: number = 0
): [val: Equality, index: number] => {
  const rest: Equality[1] = [];
  const [sum, i] = parseSum(str, index);
  index = i;
  while (true) {
    const token = str[index];
    if (!EQUALITY_OPS.includes(token as typeof EQUALITY_OPS[number])) break;
    const [restSum, i] = parseSum(str, index + 1);
    rest.push({ type: token as typeof EQUALITY_OPS[number], item: restSum });
    index = i;
  }
  return [[sum, rest], index];
};

export const parseSum = (str: Token[], index: number = 0): [val: Sum, index: number] => {
  const rest: Sum[1] = [];
  const [product, i] = parseProduct(str, index);
  index = i;

  while (true) {
    const token = str[index];
    if (token !== "+" && token !== "-") break;
    const [restProduct, i] = parseProduct(str, index + 1);
    rest.push({ type: token as "+" | "-", item: restProduct });
    index = i;
  }
  return [[product, rest], index];
};

export const parseProduct = (
  str: Token[],
  index: number = 0
): [val: Product, index: number] => {
  const rest: Product[1] = [];
  const [value, i] = parsePrefix(str, index);
  index = i;

  while (true) {
    const token = str[index];
    if (token !== "??" && token !== "*" && token !== "/") break;
    const [restValue, i] = parsePrefix(str, index + 1);
    rest.push({ type: token as "*" | "/" | "??", item: restValue });
    index = i;
  }

  return [[value, rest], index];
};

export const parsePrefix = (
  str: Token[],
  index: number = 0
): [val: Prefix, index: number] => {
  const token = str[index];
  let rest: Prefix[1];
  if (typeof token === "object" && token.type === "name" && token.item in PREFIX_OPS) {
    rest = { type: PREFIX_OPS[token.item as PrefixOp] };
    index++;
  }
  const [value, i] = parseValue(str, index);
  index = i;

  return [[value, rest], index];
};

export const parseMatchPattern = (
  str: Token[],
  index: number = 0
): [val: MatchPattern, index: number] => {
  const token = str[index];
  if (typeof token === "object" && token.type === "name" && token.item === "if") {
    const [expr, _i] = parseExpr(str, index + 1);
    {
      const token = str[_i];
      if (!(typeof token === "object" && token.type === "name" && token.item === "with"))
        throw new Error('missing keyword "with"');
      index = _i + 1;
    }

    const token = str[index];
    if (!(typeof token === "object" && token.type === "name"))
      throw new Error("missing name");

    return [{ type: "condition", item: { expr, name: token.item } }, index + 1];
  }
  if (token === "|") {
    const patterns: MatchPattern[] = [];
    while (true) {
      const token = str[index];
      if (token !== "|") break;

      const [pattern, _i] = parseMatchPattern(str, index + 1);
      index = _i;
      patterns.push(pattern);
    }
    return [{ type: "union", item: { patterns } }, index];
  }

  return parseValue(str, index);
};

export const parseValue = (
  str: Token[],
  index: number = 0
): [val: Value, index: number] => {
  const token = str[index];
  if (
    typeof token === "object" &&
    (token.type === "num" ||
      token.type === "bool" ||
      token.type === "str" ||
      token.type === "measure")
  ) {
    return [token, index + 1];
  }
  if (token === "\\" || (typeof token === "object" && token.type === "name")) {
    const [expr, _i] = parseAccessExpression(str, index);
    return [{ type: "name", item: expr }, _i];
  }
  if (token === "(") {
    const [expr, _i] = parseExpr(str, index + 1);

    const token = str[_i];
    if (token !== ")") throw new Error("no closing parens");
    return [{ type: "expr", item: expr }, _i + 1];
  }
  if (token === "match") {
    const [expr, _i] = parseExpr(str, index + 1);

    {
      const token = str[_i];
      if (token !== "{") throw new Error("no opening bracket");
      index = _i;
    }

    const patterns: { pattern: MatchPattern; expr: Expression }[] = [];
    while (true) {
      const [pattern, _i] = parseMatchPattern(str, index + 1);

      {
        const token = str[_i];
        if (token !== ":") throw new Error("no colon");
        index = _i;
      }

      const [expr, _i2] = parseExpr(str, index + 1);

      patterns.push({ pattern, expr });

      {
        const token = str[_i2];
        if (!token) throw new Error("no closing bracket");
        index = _i2;
        if (token === "}") break;
        if (token === "," && str[index + 1] === "}") break;
        if (token !== ",") throw new Error("unexpected character");
      }
    }

    return [{ type: "match", item: { patterns, value: expr } }, index];
  }

  if (token === "{") {
    index += 1;
    const items: { key: string; value: Expression }[] = [];

    while (true) {
      const token = str[index];
      assert(
        typeof token === "object" && (token.type === "name" || token.type === "str"),
        "object key is not a name or string"
      );
      const key = token.item;

      {
        const token = str[index + 1];
        assert(token === ":", "no colon");
        index += 1;
      }

      const [value, i] = parseExpr(str, index + 1);

      items.push({ key, value });

      {
        const token = str[i];
        if (!token) throw new Error("no closing bracket");
        if (token === "}") {
          index = i + 1;
          break;
        }
        if (token === ",") index = i + 1;
        else throw new Error("unexpected character");
      }
    }

    return [{ type: "object", item: items }, index];
  }
  // if (token === "[") {
  //   return [{ type: "array", item: { patterns, value: expr } }, index + 1];
  // }
  // if (token === "map") {
  //   return [{ type: "map", item: { patterns, value: expr } }, index + 1];
  // }

  throw new Error("unexpected character");
};

const evalName = (expr: AccessExpression, ctx: Context): EvalValue => {
  const path = evalAccessExpr(expr, ctx);
  const reducer = (acc: EvalValue, path: string[]): EvalValue => {
    if (path.length === 0) return acc;
    const [name, ...rest] = path;

    if (name === "_" && Array.isArray(acc))
      return acc.reduce((acc: any, value: any) => acc + reducer(value, rest), 0);
    else return reducer((acc as Context)?.[name] as EvalValue, rest);
  };
  const res = reducer(ctx, path);
  // const res = path.reduce(
  //   (acc, name) => (acc as Context)?.[name] as string | Context,
  //   ctx as string | Context
  // );

  if (res instanceof Date) return res;
  if (
    !(
      typeof res === "string" ||
      typeof res === "number" ||
      typeof res === "boolean" ||
      typeof res === "object" ||
      !res
    )
  )
    throw new Error(
      path.join(".") + " is not a primitive, found value: " + JSON.stringify(res)
    );

  // if (typeof res === "string" && !Number.isNaN(Number(res))) return Number(res);
  // if (typeof res === "string" && !Number.isNaN(new Date(res).getTime()))
  //   return new Date(res);
  return res;
};

const evalValue = (expr: Value, ctx: Context): EvalValue => {
  if (expr.type === "num" || expr.type === "bool") return expr.item;
  if (expr.type === "str") return contextReplace(expr.item, ctx);
  if (expr.type === "measure") {
    switch (expr.item.measure) {
      case "ms":
        return moment.duration(expr.item.value, "milliseconds");
      case "s":
        return moment.duration(expr.item.value, "seconds");
      case "min":
        return moment.duration(expr.item.value, "minutes");
      case "h":
        return moment.duration(expr.item.value, "hours");
      case "d":
        return moment.duration(expr.item.value, "days");
      case "mon":
        return moment.duration(expr.item.value, "months");
      case "y":
        return moment.duration(expr.item.value, "years");
    }
  }
  if (expr.type === "expr") return evalExpr(expr.item, ctx);
  if (expr.type === "array") return expr.item.map((expr) => evalExpr(expr, ctx));
  if (expr.type === "object")
    return Object.fromEntries(
      expr.item.map(({ key, value }) => [key, evalExpr(value, ctx)])
    );
  if (expr.type === "map") {
    const val = evalExpr(expr.item.value, ctx);
    if (!Array.isArray(val)) throw new Error("can't map anything other than array");
    return val.map((value: EvalValue) =>
      evalExpr(expr.item.expr, { ...ctx, [expr.item.name]: value })
    );
  }
  if (expr.type === "match") {
    const value = evalExpr(expr.item.value, ctx);
    const matcher = evalMatchPattern(value);
    const pattern = expr.item.patterns.find(({ pattern }) => matcher(pattern, ctx));
    assert(pattern, "match failed");
    return evalExpr(pattern.expr, ctx);
  }
  return evalName(expr.item, ctx);
};

const evalMatchPattern =
  (value: EvalValue) =>
  (_expr: MatchPattern, ctx: Context): EvalValue => {
    if (_expr.type === "condition") {
      const {
        item: { expr, name },
      } = _expr;

      return evalExpr(expr, { ...ctx, [name]: value });
    }
    if (_expr.type === "union") {
      const matcher = evalMatchPattern(value);
      return _expr.item.patterns.some((pattern) => matcher(pattern, ctx));
    }

    return value === evalValue(_expr, ctx);
  };

const evalPrefix = (expr: Prefix, ctx: Context): EvalValue => {
  const [value, rest] = expr;
  const val = evalValue(value, ctx);

  if (rest) {
    if (rest.type === "castDate" && val instanceof Date) return new Date(val);
    if (rest.type === "castDate" && typeof val === "number") return new Date(val);
    if (rest.type === "castDate" && typeof val === "string") return new Date(val);
    if (rest.type === "castString" && val instanceof Date) return `${val}`;
    if (rest.type === "castString" && typeof val === "number") return `${val}`;
    if (rest.type === "castString" && typeof val === "string") return `${val}`;
    if (rest.type === "castNumber" && val instanceof Date) return val.getTime();
    if (rest.type === "castNumber" && typeof val === "number") return Number(val);
    if (rest.type === "castNumber" && typeof val === "string") return Number(val);
    if (rest.type === "translate" && typeof val === "string")
      return i18next.t(val) ?? val;
    if (rest.type === "translate" && Array.isArray(val))
      return i18next.t(val[0], val[1]) ?? val;
    if (rest.type === "round" && typeof val === "number") return Math.round(val);
    if (rest.type === "ceil" && typeof val === "number") return Math.ceil(val);
    if (rest.type === "floor" && typeof val === "number") return Math.floor(val);
    if (rest.type === "not") return !val;
    throw new Error("unknown prefix operator", { cause: rest });
  }
  return val;
};

const evalProduct = (expr: Product, ctx: Context): EvalValue => {
  const [value, rest] = expr;
  const val = evalPrefix(value, ctx);

  return rest.reduce((acc, item) => {
    const right = evalPrefix(item.item, ctx);

    if (item.type === "??") return acc ?? right;

    if (moment.isDuration(right) && typeof acc === "number" && item.type === "*")
      return momentDurationMultiply(right, acc);
    if (moment.isDuration(acc) && typeof right === "number" && item.type === "*")
      return momentDurationMultiply(acc, right);

    if (moment.isDuration(right) && typeof acc === "number" && item.type === "/")
      return momentDurationDivide(right, acc);
    if (moment.isDuration(acc) && typeof right === "number" && item.type === "/")
      return momentDurationDivide(acc, right);

    assert(
      typeof right === "number" && typeof acc === "number",
      `* and / are only applicable to numbers or duration and number, got: ${acc} ${item.type} ${right}`
    );

    if (item.type === "*") return acc * right;
    return acc / right;
  }, val);
};

const evalSum = (expr: Sum, ctx: Context): EvalValue => {
  const [product, rest] = expr;
  const val = evalProduct(product, ctx);

  return rest.reduce((acc, item) => {
    let right = evalProduct(item.item, ctx);

    if (typeof acc === "string" && !Number.isNaN(Number(acc)) && acc.match(/\d/))
      acc = Number(acc);
    if (typeof right === "string" && !Number.isNaN(Number(right)) && right.match(/\d/))
      right = Number(right);
    if (typeof acc === "string" && !Number.isNaN(new Date(acc).getTime()))
      acc = new Date(acc);
    if (typeof right === "string" && !Number.isNaN(new Date(right).getTime()))
      right = new Date(right);

    if (moment.isDuration(right) && moment.isDuration(acc) && item.type === "+")
      return acc.clone().add(right);
    if (right instanceof Date && acc instanceof Date && item.type === "+")
      return new Date(acc.getTime() + right.getTime());
    if (right instanceof Date && typeof acc === "number" && item.type === "+")
      return new Date(acc + right.getTime());
    if (typeof right === "number" && acc instanceof Date && item.type === "+")
      return new Date(acc.getTime() + right);
    if (moment.isDuration(right) && acc instanceof Date && item.type === "+")
      return moment(acc).add(right).toDate();
    if (right instanceof Date && acc instanceof Date && item.type === "-")
      return new Date(acc.getTime() - right.getTime());
    if (right instanceof Date && typeof acc === "number" && item.type === "-")
      return new Date(acc - right.getTime());
    if (typeof right === "number" && acc instanceof Date && item.type === "-")
      return new Date(acc.getTime() - right);
    if (moment.isDuration(right) && acc instanceof Date && item.type === "-")
      return moment(acc).subtract(right).toDate();
    if (typeof right === "string" && typeof acc === "string" && item.type === "+")
      return acc + right;
    if (typeof right === "number" && typeof acc === "number" && item.type === "+")
      return acc + right;
    if (typeof right === "number" && typeof acc === "number" && item.type === "-")
      return acc - right;
    throw new Error(
      `cannot apply + or - with different value types, got: ${acc} ${item.type} ${right}`
    );
  }, val);
};

const evalEquality = (expr: Equality, ctx: Context): EvalValue => {
  const [sum, rest] = expr;
  const val = evalSum(sum, ctx);

  return rest.reduce((acc, item) => {
    const right = evalSum(item.item, ctx);

    if (item.type === "=") return acc === right;
    if (item.type === "!=") return acc !== right;
    if (item.type === "<") return acc < right;
    if (item.type === ">") return acc > right;
    if (item.type === ">=") return acc >= right;
    if (item.type === "<=") return acc <= right;
    if (item.type === "in")
      return (
        (acc as string) in (right as object) &&
        !!(right as Record<string, any>)[acc as string]
      );
    if (item.type === "not_in")
      return !(
        (acc as string) in (right as object) &&
        !!(right as Record<string, any>)[acc as string]
      );
    throw new Error("cant evaluate");
  }, val);
};

const evalBoolean = (expr: Boolean, ctx: Context): EvalValue => {
  const [sum, rest] = expr;
  const val = evalEquality(sum, ctx);

  return rest.reduce((acc, item) => {
    if (item.type === "and" && !acc) return false;
    if (item.type === "or" && !!acc) return true;
    const right = evalEquality(item.item, ctx);

    if (item.type === "and") return !!acc && !!right;
    if (item.type === "or") return !!acc || !!right;
    throw new Error("cant evaluate rule");
  }, val);
};

export const evalExpr = (expr: Expression, ctx: Context): EvalValue => {
  const res = evalBoolean(expr, ctx);
  return res;
};

const evalAccessExpr = (expr: AccessExpression, ctx: Context): string[] => {
  return expr.map((item) => {
    if (item.type === "name") return item.item;
    if (item.type === "subname") {
      const val = ctx[item.item];
      if (typeof val !== "string" || typeof val !== "number")
        throw new Error("must be string or number");
      return val;
    }

    return evalExpr(item.item, ctx).toString();
  });
};

export const evalAccessStr = (str: string, ctx: Context) =>
  evalAccessExpr(parseAccessExpression([...tokens(str)])[0], ctx);
export const evalExprStr = (str: string, ctx: Context) => {
  return evalExpr(parseExpr([...tokens(str)])[0], ctx);
};

function momentDurationMultiply(duration: any, by: number) {
  duration._milliseconds *= by;
  duration._days *= by;
  duration._months *= by;

  return duration._bubble();
}

function momentDurationDivide(duration: any, by: number) {
  duration._milliseconds /= by;
  duration._days /= by;
  duration._months /= by;

  return duration._bubble();
}

// @ts-ignore
window.evaluator = { evalAccessStr, evalExprStr };
